// ============================================================================
//   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
// ----------------------------------------------------------------------------
//   Shared recharts <Tooltip> styling — token-driven so it renders correctly
//   in BOTH light and dark mode (the default recharts tooltip hard-codes a
//   white background while its label/item text inherits the theme text color,
//   so in dark mode the text/date was invisible — white-on-white).
//
//   Usage:  <Tooltip {...chartTooltipStyle} formatter={...} />
//   Custom-content tooltips (content={<X/>}) must instead theme themselves
//   with the same tokens (see chartTooltipClasses).
// ============================================================================
import type { CSSProperties } from 'react';

/**
 * Spread onto a recharts <Tooltip> that uses the default renderer
 * (i.e. NOT content={<Custom/>}). Colors come only from design tokens.
 */
export const chartTooltipStyle: {
  contentStyle: CSSProperties;
  labelStyle: CSSProperties;
  itemStyle: CSSProperties;
} = {
  contentStyle: {
    backgroundColor: 'var(--color-bg-card)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    boxShadow: 'var(--shadow-md)',
    color: 'var(--color-text-primary)',
    fontSize: '12px',
    padding: '8px 10px',
  },
  labelStyle: {
    color: 'var(--color-text-primary)',
    fontWeight: 600,
    marginBottom: 2,
  },
  itemStyle: {
    color: 'var(--color-text-secondary)',
    padding: 0,
  },
};

/**
 * Tailwind classes for hand-rolled custom tooltip content components, so they
 * match the shared default tooltip in both themes.
 */
export const chartTooltipClasses =
  'rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-bg-card)] ' +
  'px-2.5 py-2 text-xs text-[var(--color-text-primary)] shadow-[var(--shadow-md)]';
